import { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { intervalPatternName } from '../../../../../common/src/interval_training.ts';
import { Clef } from '../../../../../common/src/learn_rules.ts';
import HouseIcon from '../../../assets/learn-house.svg?react';
import MicIcon from '../../../assets/learn-mic.svg?react';
import SpeakerIcon from '../../../assets/learn-speaker.svg?react';
import { ButtonColumn, learnButtonHeight, SecondaryButton, ToggleButton } from '../learn_buttons.tsx';
import { HearingYouMeter } from '../learn_meter.tsx';
import {
  attemptPips,
  IconLine,
  ProgressPipsRow,
  ScreenBody,
  ScreenFoot,
  shortScreen,
  Slack,
} from '../learn_screen_parts.tsx';
import { NoteHintBlock, noteHintColors, NoteStaffRow, SkipNoteLink } from '../pitch/note_screen_parts.tsx';
import { pitchHintAmber } from '../pitch/pitch_colors.ts';
import {
  canHearAgain,
  canPlayPatternNote,
  currentIntervalPattern,
  intervalDot,
  IntervalHint,
  intervalHint,
  intervalHintText,
  intervalHoldProgress,
  intervalNoteIndex,
  intervalOctaveText,
  intervalOnPitchCount,
  intervalPatternCount,
  intervalPatternIndex,
  intervalResultSentence,
  IntervalSessionState,
  intervalSoundView,
  intervalStepsDegrees,
  intervalSyllable,
} from './interval_session.ts';
import { intervalNoteColors, IntervalStaff, IntervalStaffDot, IntervalStaffNote } from './interval_staff.tsx';
import { intervalDegreeSpan } from './interval_staff_geometry.ts';

// One pattern of an Interval Training session (issue #3721), from the "Interval Training: listen",
// "sing it back" and "pattern result" artboards: the three phases a pattern goes through share one
// screen, so the progress row, the pattern's syllables and plain label, and the staff with its
// syllables stay put while what is under them changes.
//
//   - Listen: "Listen" with a speaker; under the staff, what is happening; then the line about the
//     house.
//   - Sing: "Your turn: sing re" with a mic; under the staff Pitch Match's hint block (the hint, the
//     octave line, the hold label and meter) and "Skip this note"; then the "Hearing you" meter.
//   - Result: "2 of 3 notes on pitch"; the staff in result colours with where a miss landed, a
//     legend and the sentence about the miss.
//
// The foot is one row throughout: the main button beside the house. The main button is one element
// whose words, look and action follow the phase - "Sing it back" (solid), then "Hear it again"
// (outlined, greyed out until the note is listening and once its replays are spent), then "Next
// pattern" or "See your score" (solid) - and the house is one element too, so neither is swapped
// for another under a finger already on it as the pattern ends (the ToggleButton rule in
// learn_buttons.tsx, issue #3518). On the result a second row under it holds "Hear it as steps",
// when there is a jump to fill, and "Sing again". The artboard has no house on the result; the issue
// asks for it on all three screens, and a row of three would not fit "Hear it as steps", so it
// stays beside the main button.
//
// Presentational: everything is read from the session's state through its selectors, and every
// button is a callback the page dispatches, so a story can render any moment of a pattern from a
// state it drives the reducer to. The page renders other screens for the summary and a failure.
// No key name, no letter names and no interval names: the syllables are the only labels.

export const intervalPatternScreenId = 'interval-pattern';
// The main button's id names what it does in the phase on screen; it is one element throughout
export const intervalSingNowButtonId = 'interval-sing-now-button';
export const intervalHearAgainButtonId = 'interval-hear-again-button';
export const intervalNextButtonId = 'interval-next-button';
export const intervalSkipButtonId = 'interval-skip-button';
export const intervalHomeButtonId = 'interval-home-button';
export const intervalStepsButtonId = 'interval-steps-button';
export const intervalSingAgainButtonId = 'interval-sing-again-button';

export const intervalPatternRowTestId = 'interval-pattern-row';
export const intervalPipTestId = 'interval-pip';
export const intervalPatternNameTestId = 'interval-pattern-name';
export const intervalPatternLabelTestId = 'interval-pattern-label';
export const intervalStatusTestId = 'interval-status';
export const intervalOnPitchTestId = 'interval-on-pitch';
export const intervalLegendTestId = 'interval-legend';
export const intervalSentenceTestId = 'interval-sentence';

export const listenHeadingText = 'Listen';
export const yourTurnText = 'Your turn: sing';
export const playingPatternText = 'Playing the pattern. Sing it back when the mic turns on.';
export const houseHintText = 'Tap the house any time to hear do, the home note.';
export const singItBackText = 'Sing it back';
export const hearItAgainText = 'Hear it again';
export const homeButtonLabel = 'Hear do, the home note';
export const notesOnPitchText = 'notes on pitch';
export const nextPatternText = 'Next pattern';
export const seeYourScoreText = 'See your score';
export const hearAsStepsText = 'Hear it as steps';
export const singAgainText = 'Sing again';
export const legendTexts = { onPitch: 'on pitch', close: 'close', missed: 'missed' } as const;

export function patternCountText(index: number, count: number): string {
  return `Pattern ${index + 1} of ${count}`;
}

export function onPitchCountText(onPitch: number, notes: number): string {
  return `${onPitch} of ${notes}`;
}

// The hint's colour, as Pitch Match colours its hints (note_screen_parts.tsx), and the amber of a
// note that ended close, which Pitch Match does not have
const hintColors: Record<IntervalHint, string> = { ...noteHintColors, close: pitchHintAmber };

interface Callbacks {
  onSingNow: () => void; // "Sing it back"
  onHearAgain: () => void;
  onSkip: () => void;
  onHome: () => void; // the house
  onTapSyllable: (index: number) => void; // a syllable under the staff, by its place in the pattern
  onNext: () => void; // "Next pattern", or "See your score" after the last
  onSteps: () => void; // "Hear it as steps"
  onSingAgain: () => void;
}

export const IntervalPatternScreen = ({
  state,
  clef,
  ...callbacks
}: {
  state: IntervalSessionState; // in listen, sing or result
  clef: Clef; // the singer's, from clefForVoicePart
} & Callbacks) => {
  const { phase } = state;
  const pattern = currentIntervalPattern(state);
  const patternIndex = intervalPatternIndex(state);
  const patternCount = intervalPatternCount(state);
  // The session's span only changes with its patterns; the screen renders on every mic frame
  const { lowDegree, highDegree } = useMemo(() => intervalDegreeSpan(state.patterns), [state.patterns]);
  const soundView = intervalSoundView(state);
  return (
    <ScreenBody
      id={intervalPatternScreenId}
      data-phase={phase}
      data-pattern-index={patternIndex}
      data-note-index={intervalNoteIndex(state)}
      data-home-sounding={soundView.homeSounding ? 'true' : 'false'}
    >
      <ProgressPipsRow
        label={patternCountText(patternIndex, patternCount)}
        pips={attemptPips(patternIndex, patternCount)}
        rowTestId={intervalPatternRowTestId}
        pipTestId={intervalPipTestId}
      />
      <PatternName data-testid={intervalPatternNameTestId}>{intervalPatternName(pattern)}</PatternName>
      <PatternLabel data-testid={intervalPatternLabelTestId}>{pattern.label}</PatternLabel>
      {phase === 'result' ? <OnPitchCount state={state} /> : <StatusLine state={state} />}
      <NoteStaffRow>
        <IntervalStaff
          clef={clef}
          homeMidi={state.homeMidi}
          lowDegree={lowDegree}
          highDegree={highDegree}
          notes={staffNotes(state, soundView)}
          dot={staffDot(state)}
          syllablesPlay={canPlayPatternNote(state)}
          onTapSyllable={callbacks.onTapSyllable}
        />
      </NoteStaffRow>
      {/* One slot per phase, so the foot after them stays the same element from phase to phase */}
      {phase === 'listen' && <ListenParts />}
      {phase === 'sing' && <SingParts state={state} onSkip={callbacks.onSkip} />}
      {phase === 'result' && <ResultParts state={state} />}
      <Foot state={state} homeSounding={soundView.homeSounding} {...callbacks} />
    </ScreenBody>
  );
};

// Each note as the staff draws it: its own state, whether it is sounding now (a note of the
// pattern as it plays or a syllable tapped, which emphasises it without taking its colour), and on
// the result where a miss or a close note landed
function staffNotes(
  state: IntervalSessionState,
  { displays, litIndex }: ReturnType<typeof intervalSoundView>,
): IntervalStaffNote[] {
  return currentIntervalPattern(state).degrees.map((degree, index) => ({
    degree,
    display: displays[index],
    sounding: litIndex === index,
    landedCents: state.phase === 'result' ? state.sung[index]?.landedCents : undefined,
  }));
}

// The singer's dot belongs to the note being sung, so it is drawn only while singing. How far the
// hold has filled slides it toward the next note: the session's own hold, kept through a breath and
// dropped after it, and full from the lock through the pause, so the dot needs no state of its own.
function staffDot(state: IntervalSessionState): IntervalStaffDot | undefined {
  const dot = state.phase === 'sing' ? intervalDot(state) : undefined;
  if (!dot) {
    return undefined;
  }
  const { heldMs, holdMs } = intervalHoldProgress(state);
  return { index: intervalNoteIndex(state), ...dot, held: holdMs > 0 ? heldMs / holdMs : 0 };
}

const StatusLine = ({ state }: { state: IntervalSessionState }) => {
  if (state.phase === 'listen') {
    return (
      <Status data-testid={intervalStatusTestId}>
        <SpeakerIcon aria-hidden="true" />
        {listenHeadingText}
      </Status>
    );
  }
  return (
    <Status data-testid={intervalStatusTestId}>
      <MicIcon aria-hidden="true" />
      {yourTurnText} <StatusSyllable>{intervalSyllable(state)}</StatusSyllable>
    </Status>
  );
};

const OnPitchCount = ({ state }: { state: IntervalSessionState }) => (
  <CountRow data-testid={intervalOnPitchTestId}>
    <CountNumber>
      {onPitchCountText(intervalOnPitchCount(state), currentIntervalPattern(state).degrees.length)}
    </CountNumber>
    <CountLabel>{notesOnPitchText}</CountLabel>
  </CountRow>
);

const ListenParts = () => (
  <>
    <Caption>{playingPatternText}</Caption>
    <Slack />
    <IconLine>
      <HouseIcon aria-hidden="true" />
      <span>{houseHintText}</span>
    </IconLine>
  </>
);

const SingParts = ({ state, onSkip }: { state: IntervalSessionState; onSkip: () => void }) => {
  const note = state.note;
  const hint = intervalHint(state);
  const { heldMs, holdMs } = intervalHoldProgress(state);
  // The meter fills green on a reading that counts, exactly as the dot is coloured
  const counting = note.outcome?.kind === 'onPitch' || intervalDot(state)?.color === 'green';
  return (
    <>
      <NoteHintBlock
        hint={hint}
        text={intervalHintText(hint, intervalSyllable(state), canHearAgain(state))}
        color={hintColors[hint]}
        octaveText={intervalOctaveText(state)}
        heldMs={heldMs}
        holdMs={holdMs}
        counting={counting}
      />
      <SkipNoteLink id={intervalSkipButtonId} disabled={note.outcome !== undefined} onClick={onSkip} />
      <Slack />
      <HearingYouMeter level={note.level} />
    </>
  );
};

const ResultParts = ({ state }: { state: IntervalSessionState }) => {
  const sentence = intervalResultSentence(state);
  return (
    <>
      <Legend data-testid={intervalLegendTestId}>
        {(['onPitch', 'close', 'missed'] as const).map((result) => (
          <LegendItem key={result}>
            <LegendDot style={{ backgroundColor: intervalNoteColors[result] }} />
            {legendTexts[result]}
          </LegendItem>
        ))}
      </Legend>
      {sentence && <Sentence data-testid={intervalSentenceTestId}>{sentence}</Sentence>}
      <Slack />
    </>
  );
};

// What the main button is in each phase: its id, words, look, whether it can be tapped, and what a
// tap does
function mainButton(state: IntervalSessionState, callbacks: Callbacks) {
  if (state.phase === 'listen') {
    return {
      id: intervalSingNowButtonId,
      text: singItBackText,
      solid: true,
      enabled: true,
      onClick: callbacks.onSingNow,
    };
  }
  if (state.phase === 'sing') {
    return {
      id: intervalHearAgainButtonId,
      text: hearItAgainText,
      solid: false,
      enabled: canHearAgain(state),
      onClick: callbacks.onHearAgain,
    };
  }
  const last = intervalPatternIndex(state) >= intervalPatternCount(state) - 1;
  return {
    id: intervalNextButtonId,
    text: last ? seeYourScoreText : nextPatternText,
    solid: true,
    enabled: true,
    onClick: callbacks.onNext,
  };
}

// The buttons, one row throughout and a second on the result (see the header)
const Foot = ({
  state,
  homeSounding,
  ...callbacks
}: { state: IntervalSessionState; homeSounding: boolean } & Callbacks) => {
  const main = mainButton(state, callbacks);
  const steps = state.phase === 'result' && intervalStepsDegrees(state) !== undefined;
  return (
    <ScreenFoot>
      <ButtonColumn>
        <ButtonRow>
          <ToggleButton
            id={main.id}
            data-testid={main.id}
            $solid={main.solid}
            disabled={!main.enabled}
            onClick={main.onClick}
          >
            {main.text}
          </ToggleButton>
          <HouseButton sounding={homeSounding} onClick={callbacks.onHome} />
        </ButtonRow>
        {state.phase === 'result' && (
          <ButtonRow>
            {steps && (
              <SecondaryButton
                id={intervalStepsButtonId}
                data-testid={intervalStepsButtonId}
                onClick={callbacks.onSteps}
              >
                {hearAsStepsText}
              </SecondaryButton>
            )}
            <SecondaryButton
              id={intervalSingAgainButtonId}
              data-testid={intervalSingAgainButtonId}
              onClick={callbacks.onSingAgain}
            >
              {singAgainText}
            </SecondaryButton>
          </ButtonRow>
        )}
      </ButtonColumn>
    </ScreenFoot>
  );
};

// The square outlined house: do, the home note, on every phase of a pattern. It pulses while its do
// sounds.
const HouseButton = ({ sounding, onClick }: { sounding: boolean; onClick: () => void }) => (
  <HouseSquare
    id={intervalHomeButtonId}
    data-testid={intervalHomeButtonId}
    data-sounding={sounding ? 'true' : 'false'}
    aria-label={homeButtonLabel}
    $sounding={sounding}
    onClick={onClick}
  >
    <HouseIcon aria-hidden="true" />
  </HouseSquare>
);

const PatternName = styled.div`
  margin-top: 14px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 22px;
  line-height: 30px;

  ${shortScreen} {
    margin-top: 8px;
    font-size: 20px;
    line-height: 26px;
  }
`;

const PatternLabel = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-darker);
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 17px;
  line-height: 24px;
  color: var(--color-tab-selected);

  svg {
    flex: none;
    width: 22px;
    height: 22px;
  }

  ${shortScreen} {
    margin-top: 6px;
  }
`;

const StatusSyllable = styled.span`
  font-size: 20px;
`;

const CountRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 10px;

  ${shortScreen} {
    margin-top: 6px;
  }
`;

const CountNumber = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 34px;
  line-height: 1;
  color: var(--color-tab-selected);

  ${shortScreen} {
    font-size: 30px;
  }
`;

const CountLabel = styled.div`
  font-size: 15px;
  line-height: 20px;
  color: var(--color-text-darker);
`;

const Caption = styled.div`
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: var(--color-text-darker);
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 6px;
`;

const Sentence = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  background-color: var(--color-background-search);
  font-size: 15px;
  line-height: 21px;

  ${shortScreen} {
    margin-top: 10px;
    padding: 10px 12px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
`;

const pulse = keyframes`
  from { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-tab-selected) 45%, transparent); }
  to { box-shadow: 0 0 0 10px color-mix(in srgb, var(--color-tab-selected) 0%, transparent); }
`;

const HouseSquare = styled.button<{ $sounding: boolean }>`
  flex: none;
  box-sizing: border-box;
  width: ${learnButtonHeight}px;
  height: ${learnButtonHeight}px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--color-tab-selected);
  border-radius: 10px;
  background-color: ${({ $sounding }) =>
    $sounding ? 'color-mix(in srgb, var(--color-tab-selected) 16%, transparent)' : 'transparent'};
  color: var(--color-tab-selected);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &[data-sounding='true'] {
    animation: ${pulse} 900ms ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-sounding='true'] {
      animation: none;
    }
  }

  &:active {
    background-color: color-mix(in srgb, var(--color-tab-selected) 12%, transparent);
  }

  svg {
    width: 26px;
    height: 26px;
  }
`;
